import type { VoidComponent } from "solid-js";
import { createMemo, getOwner, runWithOwner } from "solid-js";
import posthog from "posthog-js";
import { useTranslation } from "../../../state/i18n";
import InputModal from "../../../components/modal/InputModal";
import type { DataSourceType } from "../../../types/DataSource";
import { dataSourcesByType } from "./dataSourcesByType";
import { useNavigate } from "@solidjs/router";
import { useCustomerState } from "../../../state/customer";
import ApiService from "../../../services/ApiService";
import { useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import { POSTHOG_EVENTS } from "../../../services/PostHogService";

type CreateDataSourceModalProps = {
  type: DataSourceType;
};
const CreateDataSourceModal: VoidComponent<CreateDataSourceModalProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { replace } = useModalManager();
  const customer = useCustomerState();
  const definition = createMemo(() => dataSourcesByType[props.type]);

  return (
    <InputModal
      titleIcon="database"
      title={t(($) => $.CREATE_DATASOURCE_MODAL_TITLE, { type: definition().name })}
      variant="default"
      size="large"
      confirmLabel={t(($) => $.CREATE_DATASOURCE_MODAL_ACTIONS_ADD)}
      cancelLabel={t(($) => $.CREATE_DATASOURCE_MODAL_ACTIONS_CANCEL)}
      schema={definition().schema}
      initialValue={definition().initialValue}
      onConfirm={(value) => {
        const owner = getOwner();
        const { toDto, onCreated } = definition();

        return new Promise((resolve, reject) => {
          ApiService.createDataSource().mutate(toDto(value), {
            onSuccess: (dataSource) => {
              runWithOwner(owner, () => {
                onCreated?.(dataSource, value);
              });

              posthog.capture(POSTHOG_EVENTS.DATA_SOURCE_CREATED, { type: props.type });
              navigate(`/customers/${customer.customerId}/data-sources/${dataSource.id}`);
              resolve();
            },
            onError: (error) => {
              replace({ content: () => <GenericFailureModal error={error} /> });
              reject(error);
            },
          });
        });
      }}
    >
      {(input) => definition().EditItem({ ...input, type: "CREATE" })}
    </InputModal>
  );
};

export default CreateDataSourceModal;
